import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, User, Search, LogOut, LayoutDashboard, Package, ChevronDown, ArrowRight, Sparkles } from 'lucide-react';
import { motion, useScroll, useSpring, AnimatePresence } from 'framer-motion';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Magnetic, Logo } from '@/components/motion/Primitives';
import { resolveImage } from '@/lib/format';

const CATEGORIES = [
    { name: 'Laundry Care', desc: 'Detergents & softeners', image: '/products/detergent-liquid-bottle.jpg', to: '/shop?category=Laundry%20Care' },
    { name: 'Dishwashing', desc: 'Gels & bars that degrease', image: '/products/dishwash-gel-real.jpg', to: '/shop?category=Dishwashing' },
    { name: 'Surface & Glass', desc: 'Streak-free shine', image: '/products/glass-cleaner-real.jpg', to: '/shop?category=Surface%20%26%20Glass' },
    { name: 'Bathroom & Utility', desc: 'Deep clean & descale', image: '/products/bathroom-gel.jpg', to: '/shop?category=Bathroom%20%26%20Utility' },
    { name: 'Hand Wash', desc: 'Soft, germ-free hands', image: '/products/handwash-luxe.jpg', to: '/shop?category=Hand%20Wash' },
];

const PROMO = ['Har Din, Thoda Xtra', 'Since 1987 \u00b7 Trusted by India', 'Daam Wahi. Dum Xtra.', 'Free shipping over \u20b9499', 'Kills 99.9% germs'];

// White nav label with sliding yellow underline (hover / focus / active)
const navLink = 'group relative px-3.5 py-2 rounded-full text-sm font-semibold text-white/90 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--xtra-yellow)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--xtra-blue)]';
const NavUnderline = () => (
    <span className="pointer-events-none absolute left-3.5 right-3.5 bottom-1 h-[2px] rounded-full bg-[var(--xtra-yellow)] origin-left scale-x-0 transition-transform duration-200 ease-out group-hover:scale-x-100" />
);

export default function Navbar() {
    const { count, setDrawerOpen } = useCart();
    const { user, logout, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [scrolled, setScrolled] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mega, setMega] = useState(false);
    const lastY = useRef(0);
    const megaTimer = useRef(null);

    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;
            setScrolled(y > 8);
            if (y > lastY.current && y > 160) { setHidden(true); setMega(false); }
            else setHidden(false);
            lastY.current = y;
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const openMega = () => { clearTimeout(megaTimer.current); setMega(true); };
    const closeMega = () => { megaTimer.current = setTimeout(() => setMega(false), 140); };

    const submitSearch = (e) => {
        e.preventDefault();
        navigate(`/shop?search=${encodeURIComponent(q)}`);
        setQ('');
    };

    return (
        <motion.header
            data-testid="site-nav"
            initial={{ y: 0 }}
            animate={{ y: hidden ? '-100%' : '0%' }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="sticky top-0 z-50"
        >
            {/* Promo marquee - deeper blue for hierarchy */}
            <div className="bg-[var(--xtra-blue-ink)] text-white text-xs sm:text-sm overflow-hidden pause-on-hover">
                <div className="marquee py-1.5">
                    {[0, 1].map((k) => (
                        <span key={k} className="flex">
                            {PROMO.map((t, i) => (
                                <span key={i} className="mx-5 inline-flex items-center gap-2 font-medium"><span className="text-[var(--xtra-yellow)]">✦</span>{t}</span>
                            ))}
                        </span>
                    ))}
                </div>
            </div>

            {/* Main nav - brand blue bar */}
            <div className={`bg-[var(--xtra-blue)] border-b border-white/10 transition-[box-shadow] duration-300 ${scrolled ? 'shadow-[0_10px_30px_rgba(19,2,169,0.35)]' : ''}`}>
                <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
                    <div className={`flex items-center justify-between gap-4 transition-[height] duration-300 ${scrolled ? 'h-16' : 'h-[76px]'}`}>
                        {/* Left: mobile menu + logo */}
                        <div className="flex items-center gap-2">
                            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                                <SheetTrigger asChild>
                                    <button data-testid="mobile-menu-button" className="lg:hidden p-2 -ml-2 rounded-xl text-white hover:bg-white/10 transition-colors" aria-label="Open menu">
                                        <Menu className="h-6 w-6" />
                                    </button>
                                </SheetTrigger>
                                <SheetContent side="left" className="w-[88vw] max-w-sm p-0">
                                    <SheetHeader className="px-5 py-4 border-b border-[var(--border)]">
                                        <SheetTitle className="font-display flex items-center"><Logo variant="light" className="h-6" /></SheetTitle>
                                        <SheetDescription className="sr-only">Site navigation menu</SheetDescription>
                                    </SheetHeader>
                                    <nav className="p-4 flex flex-col gap-1 overflow-y-auto">
                                        <Link to="/shop" onClick={() => setMobileOpen(false)} className="px-3 py-3 rounded-xl hover:bg-[var(--bg-soft)] font-semibold">Shop All</Link>
                                        <p className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--text-muted)]">Categories</p>
                                        {CATEGORIES.map((c) => (
                                            <Link key={c.to} to={c.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[var(--bg-soft)]">
                                                <span className="h-10 w-10 rounded-lg bg-[var(--bg-soft)] grid place-items-center overflow-hidden"><img src={resolveImage(c.image)} alt="" loading="lazy" decoding="async" className="h-8 object-contain" /></span>
                                                <span className="font-medium">{c.name}</span>
                                            </Link>
                                        ))}
                                        <div className="h-px bg-[var(--border)] my-2" />
                                        {user ? (
                                            <>
                                                <Link to="/account" onClick={() => setMobileOpen(false)} className="px-3 py-3 rounded-xl hover:bg-[var(--bg-soft)] font-medium">My Account</Link>
                                                {isAdmin && <Link to="/admin" onClick={() => setMobileOpen(false)} className="px-3 py-3 rounded-xl hover:bg-[var(--bg-soft)] font-medium">Admin Dashboard</Link>}
                                                <button onClick={() => { logout(); setMobileOpen(false); }} className="px-3 py-3 rounded-xl hover:bg-[var(--bg-soft)] font-medium text-left">Log out</button>
                                            </>
                                        ) : (
                                            <>
                                                <Link to="/login" onClick={() => setMobileOpen(false)} className="px-3 py-3 rounded-xl hover:bg-[var(--bg-soft)] font-medium">Log in</Link>
                                                <Link to="/signup" onClick={() => setMobileOpen(false)} className="btn-primary px-3 py-3">Create account</Link>
                                            </>
                                        )}
                                    </nav>
                                </SheetContent>
                            </Sheet>

                            <Link to="/" data-testid="nav-logo" className="flex items-center focus-ring rounded-md transition-transform duration-200 hover:scale-[1.03]">
                                <Logo variant="dark" className={`transition-all duration-300 ${scrolled ? 'h-6' : 'h-7'}`} />
                            </Link>
                        </div>

                        {/* Center: nav + mega menu */}
                        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
                            <Link to="/shop" data-testid="nav-link-shop" className={navLink}>Shop All<NavUnderline /></Link>
                            <div className="relative" onMouseEnter={openMega} onMouseLeave={closeMega}>
                                <button
                                    data-testid="nav-mega-menu-trigger"
                                    aria-expanded={mega}
                                    aria-haspopup="true"
                                    onFocus={openMega}
                                    onClick={() => { setMega(false); navigate('/shop'); }}
                                    className={`${navLink} inline-flex items-center gap-1`}
                                >
                                    Categories <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mega ? 'rotate-180' : ''}`} />
                                    <NavUnderline />
                                </button>
                                <AnimatePresence>
                                    {mega && (
                                        <motion.div
                                            data-testid="nav-mega-menu"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 8 }}
                                            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                                            className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+10px)] w-[720px] rounded-[var(--radius-lg)] bg-white border border-[var(--border)] shadow-[var(--shadow-soft)] p-3 z-50"
                                        >
                                            <div className="grid grid-cols-2 gap-1.5">
                                                {CATEGORIES.map((c) => (
                                                    <Link key={c.to} to={c.to} onClick={() => setMega(false)} className="group flex items-center gap-3 rounded-2xl p-2.5 hover:bg-[var(--bg-soft)] transition-colors">
                                                        <span className="h-14 w-14 rounded-xl bg-[var(--bg-soft)] grid place-items-center overflow-hidden shrink-0">
                                                            <img src={resolveImage(c.image)} alt="" loading="lazy" decoding="async" className="h-12 object-contain transition-transform duration-300 group-hover:scale-110" />
                                                        </span>
                                                        <span className="min-w-0">
                                                            <span className="block font-display font-bold text-[15px] text-black">{c.name}</span>
                                                            <span className="block text-xs text-[var(--text-muted)]">{c.desc}</span>
                                                        </span>
                                                    </Link>
                                                ))}
                                                <Link to="/shop" onClick={() => setMega(false)} className="group relative overflow-hidden flex flex-col justify-between rounded-2xl p-4 bg-[var(--xtra-blue)] text-white">
                                                    <div className="noise-overlay" />
                                                    <div className="relative">
                                                        <Sparkles className="h-5 w-5 text-[var(--xtra-yellow)]" />
                                                        <p className="font-display font-extrabold text-lg mt-2 leading-tight">Shop the<br />full range</p>
                                                    </div>
                                                    <span className="relative inline-flex items-center gap-1 text-sm font-semibold text-[var(--xtra-yellow)]">Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                                                </Link>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                            <a href="/#reviews" className={navLink}>Reviews<NavUnderline /></a>
                            <a href="/#why" className={navLink}>Why Xtra<NavUnderline /></a>
                        </nav>

                        {/* Right: search, account, cart */}
                        <div className="flex items-center gap-1.5">
                            <form onSubmit={submitSearch} className="hidden md:flex items-center relative">
                                <Search className="h-4 w-4 absolute left-3.5 text-white/70 pointer-events-none" />
                                <Input
                                    data-testid="nav-search-input"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search products"
                                    className="pl-10 h-10 w-40 lg:w-56 rounded-full bg-white/10 border-white/25 text-white placeholder:text-white/65 focus-visible:bg-white/20 focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-[var(--xtra-yellow)]"
                                />
                            </form>

                            {user ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button data-testid="account-menu-button" className="p-2.5 rounded-full text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--xtra-yellow)]" aria-label="Account">
                                            <User className="h-5 w-5" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <DropdownMenuLabel className="truncate">Hi, {user.name}</DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => navigate('/account')}><Package className="h-4 w-4 mr-2" /> My Orders</DropdownMenuItem>
                                        {isAdmin && <DropdownMenuItem onClick={() => navigate('/admin')}><LayoutDashboard className="h-4 w-4 mr-2" /> Admin</DropdownMenuItem>}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={logout}><LogOut className="h-4 w-4 mr-2" /> Log out</DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <Link to="/login" data-testid="nav-account-button" className="p-2.5 rounded-full text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--xtra-yellow)]" aria-label="Log in">
                                    <User className="h-5 w-5" />
                                </Link>
                            )}

                            <Magnetic strength={0.25}>
                                <button data-testid="nav-cart-button" onClick={() => setDrawerOpen(true)} className="relative p-2.5 rounded-full text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--xtra-yellow)]" aria-label="Open cart">
                                    <ShoppingCart className="h-5 w-5" />
                                    {count > 0 && (
                                        <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 grid place-items-center rounded-full bg-[var(--xtra-yellow)] text-black text-[11px] font-extrabold tabular-nums">{count}</span>
                                    )}
                                </button>
                            </Magnetic>
                        </div>
                    </div>
                </div>
                {/* Scroll progress - yellow for visibility on blue */}
                <motion.div style={{ scaleX: progress }} className="origin-left h-[3px] bg-gradient-to-r from-[var(--xtra-yellow)] to-white" />
            </div>
        </motion.header>
    );
}
